import type { StorageAdapter } from 'ProdantixSdk/core/types';

export class MemoryStorage implements StorageAdapter {
  private readonly store = new Map<string, string>();

  getItem(key: string): string | null {
    return this.store.get(key) ?? null;
  }

  removeItem(key: string): void {
    this.store.delete(key);
  }

  setItem(key: string, value: string): void {
    this.store.set(key, value);
  }
}

/** A store that keeps everything in memory until it is opened, then writes
 *  through to the real one; nothing held before is carried over, so an id
 *  made before consent is never joined to one made after. A key `passes`
 *  names always reaches the real store, like the consent answer itself. */
export class GatedStorage implements StorageAdapter {
  private readonly held = new MemoryStorage();
  private opened = false;

  constructor(
    private readonly real: StorageAdapter,
    private readonly passes: (key: string) => boolean = () => false,
  ) {}

  get isOpen(): boolean {
    return this.opened;
  }

  open(): void {
    this.opened = true;
  }

  close(): void {
    this.opened = false;
  }

  private target(key: string): StorageAdapter {
    return this.opened || this.passes(key) ? this.real : this.held;
  }

  getItem(key: string): string | null {
    return this.target(key).getItem(key);
  }

  removeItem(key: string): void {
    this.target(key).removeItem(key);
  }

  setItem(key: string, value: string): void {
    this.target(key).setItem(key, value);
  }
}

export function webStorage(): StorageAdapter {
  try {
    const candidate = (globalThis as { localStorage?: StorageAdapter }).localStorage;
    if (candidate) {
      const probe = '__pdx_probe__';
      candidate.setItem(probe, '1');
      candidate.removeItem(probe);
      return candidate;
    }
  } catch {
    return new MemoryStorage();
  }
  return new MemoryStorage();
}
